import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";

type GradeAdjective = {
    abbrev?: string;
    answer?: string | number;
};

type GradeData = {
    test?: { id?: number | string };
    isSocio?: boolean;
    adjectives?: GradeAdjective[];
    opsi?: string;
    colorSheets?: Record<string, number> & { count?: number };
    wordPairs?: unknown[];
    wordPairString?: string;
    combos?: Record<string, number>;
};

type TestGradingModalProps = {
    isOpen: boolean;
    onClose: () => void;
    testId?: number | string;
};

export default function TestGradingModal({ isOpen, onClose, testId }: TestGradingModalProps) {
    const [gradingData, updateGradingData] = useState<GradeData | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadGradingData();
        }
    }, [isOpen, testId]);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscapeKey);
        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    async function loadGradingData() {
        if (!testId) return;
        const resp = await RightTimeApi.getAsync(`/Grade/${testId}`);
        if (resp && resp.ok) {
            updateGradingData((resp.jsonData || null) as GradeData | null);
        }
    }

    function getMotivationLevel(value: number) {
        if (value < -1.3) return "VL";
        if (value < -0.5) return "L";
        if (value < 0.51) return "";
        if (value < 1.3) return "H";
        return "VH";
    }

    function getThinkingLevel(value: number) {
        if (value < -1.3) return "VL";
        if (value < -0.5) return "L";
        if (value < 0.51) return "M";
        if (value < 1.3) return "H";
        return "VH";
    }

    let socioText = "";
    if (gradingData) {
        socioText = gradingData.isSocio ? "Yes" : "No";
        if (gradingData.adjectives) {
            const hon = gradingData.adjectives.find((a) => a.abbrev === "HON");
            const car = gradingData.adjectives.find((a) => a.abbrev === "CAR");
            const dep = gradingData.adjectives.find((a) => a.abbrev === "DEP");
            socioText = `${socioText} HON-${hon?.answer ?? ""} CAR-${car?.answer ?? ""} DEP-${dep?.answer ?? ""}`;
        }
    }

    const motivationKeys = ["PW", "ID", "CY", "AC", "OD", "SV", "IM", "SC", "SS", "VG", "BT", "PA", "TQ"];
    const thinkingKeys = ["PT", "GD", "FOC", "PRB", "HOS"];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Test Grading Details"
            className="testGradingModal"
            size="lg"
            footer={(
                <div className="footerRight">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            )}
        >
                {gradingData ? (
                    <div className="testInfo">
                        <table className="testInfo">
                            <caption>Grading Info</caption>
                            <tbody>
                                <tr>
                                    <td>TestId</td>
                                    <td>{gradingData.test?.id}</td>
                                </tr>
                                <tr>
                                    <td>IsSocio</td>
                                    <td>{socioText}</td>
                                </tr>
                                {gradingData.opsi && (
                                    <tr>
                                        <td>OPSI</td>
                                        <td>{gradingData.opsi}</td>
                                    </tr>
                                )}
                                {(gradingData.colorSheets?.count || 0) > 0 && (
                                    <tr>
                                        <td>Colors</td>
                                        <td>
                                            Y{gradingData.colorSheets?.["Yellow"] || 0} R{gradingData.colorSheets?.["Red"] || 0} B{gradingData.colorSheets?.["Blue"] || 0} G{gradingData.colorSheets?.["Green"] || 0}
                                        </td>
                                    </tr>
                                )}
                                {((gradingData.wordPairs && gradingData.wordPairs.length > 0) || gradingData.wordPairString) && (
                                    <tr>
                                        <td>WordPairs</td>
                                        <td>{gradingData.wordPairString}</td>
                                    </tr>
                                )}
                                {gradingData.combos && (
                                    <tr>
                                        <td>Combos</td>
                                        <td className="verticalTop">
                                            <table className="motivationTable">
                                                <caption>Motivation</caption>
                                                <tbody>
                                                    {Object.entries(gradingData.combos)
                                                        .filter(([key]) => motivationKeys.includes(key))
                                                        .map(([key, value]) => (
                                                            <tr key={key}>
                                                                <th>{key}</th>
                                                                <td className="right">{value}</td>
                                                                <td>{getMotivationLevel(Number(value))}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                            <table className="thinkingTable">
                                                <caption>Thinking</caption>
                                                <tbody>
                                                    {Object.entries(gradingData.combos)
                                                        .filter(([key]) => thinkingKeys.includes(key))
                                                        .map(([key, value]) => (
                                                            <tr key={key}>
                                                                <th>{key}</th>
                                                                <td className="right">{value}</td>
                                                                <td>{getThinkingLevel(Number(value))}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <span className="processingMessage">Calculating...</span>
                )}
        </Modal>
    );
}
